import React, { useState, useRef, useCallback } from 'react';

export default function MoveItemsModal({
  isOpen,                     // ← controls visibility
  onClose,
  moveSections,
  moveSelectedSectionId,
  setMoveSelectedSectionId,
  selectedSaleId,
  clientId,
  fetchWrapper,
  onSuccess,      
  selectedItemIds,
  moveError,
}) {
  const [loading, setLoading] = useState(false);


  if (!isOpen) return null;

  const handleConfirm = async () => {
    if (!moveSelectedSectionId) {
      return;
    }

    setLoading(true);

    try {
      // Find the target section object
      const targetSection = moveSections.find(s => s.id === moveSelectedSectionId);
      if (!targetSection) throw new Error('Target section not found');

      let finalSectionId = moveSelectedSectionId;

      // If the section is not already assigned to this sale, assign it first
      if (!targetSection.is_assigned) {
        const assignResp = await fetchWrapper(
          `${process.env.REACT_APP_API_URL}/supabase/rpc/insert_sale_section`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              p_sale_id: selectedSaleId,
              p_name: targetSection.name,
            }),
          }
        );

        if (!assignResp.ok) {
          const err = await assignResp.json().catch(() => ({}));
          throw new Error(err.error?.message || 'Failed to assign section to sale');
        }

        finalSectionId = await assignResp.json();
        console.log('Assigned new section ID:', finalSectionId);
      }

      // Now move the items
      const itemIds = selectedItemIds;  // ← use the passed IDs
      if (itemIds.length === 0) {
        return;
}

      const moveResp = await fetchWrapper(
        `${process.env.REACT_APP_API_URL}/supabase/rpc/move_inventory_items_to_section`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            p_client_id: clientId,
            p_sale_id: selectedSaleId,
            p_item_ids: itemIds,
            p_new_section_id: finalSectionId,
            p_changed_by: clientId,
          }),
        }
      );

      if (!moveResp.ok) {
        const err = await moveResp.json().catch(() => ({}));
        throw new Error(err.error?.message || 'Move failed');
      }

      onSuccess?.();  // reload main grid
      onClose();
    } catch (err) {
      console.error('Move error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-40">
      <div className="bg-white rounded-lg shadow-lg max-w-md w-full mx-4 p-4">
        <h2 className="text-lg font-semibold mb-2 text-center">
          Move Selected Items to Another Section
        </h2>
        <p className="text-xs text-gray-600 mb-3 text-center">
          Choose a section to move the selected inventory items into.
        </p>

        {moveError && (
          <p className="text-xs text-red-500 mb-2 text-center">{moveError}</p>
        )}

        <div className="mb-4">
          <label
            htmlFor="move-section-select"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Target Section
          </label>
          {moveError && (
              <p className="text-xs text-red-600 mb-2 text-center font-medium">
                {moveError}
              </p>
            )}
          <select
            id="move-section-select"
            value={moveSelectedSectionId}
            onChange={(e) => setMoveSelectedSectionId(e.target.value)}
            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
          >
            <option value="">-- Select a Section --</option>
            {moveSections.map((section) => (
              <option key={section.id || section.name} value={section.id}>
                  {section.name} {section.is_assigned ? "🏷️" : ""}
                </option>
            ))}
          </select>
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100"
            disabled={loading}
          >
            Cancel
          </button>
         <button
            type="button"
            onClick={handleConfirm}
            disabled={loading || selectedItemIds.length === 0 || moveSections.length === 0 || !moveSelectedSectionId}
            className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100"
          >
            {loading ? 'Moving…' : `Move ${selectedItemIds.length} Items`}
          </button>
        </div>
      </div>
    </div>
  );
}